import React, { useEffect, useState } from 'react';
import Icon from '../Icon';
import api from '../../lib/api';
import { panelCls, primaryBtn, SectionHead } from './ui';

const LABELS = {
  improve_fitness: 'Improve my fitness',
  manage_weight: 'Manage my weight',
  improve_sleep: 'Sleep better',
  build_healthier_habits: 'Build healthier habits',
  improve_energy: 'Have more energy',
  support_healthy_ageing: 'Support healthy ageing',
  reduce_stress: 'Feel less stressed',
  improve_nutrition: 'Eat better',
};

const ICONS = {
  improve_fitness: 'fitness_center',
  manage_weight: 'monitor_weight',
  improve_sleep: 'bedtime',
  build_healthier_habits: 'checklist',
  improve_energy: 'bolt',
  support_healthy_ageing: 'elderly',
  reduce_stress: 'self_improvement',
  improve_nutrition: 'restaurant',
};

export default function GoalsPanel() {
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    (async () => {
      const { data } = await api.get('/me/goals');
      setOptions(data.options || []);
      setSelected(data.goals || []);
    })();
  }, []);

  const toggle = (g) => {
    setMessage('');
    setSelected((s) => (s.includes(g) ? s.filter((x) => x !== g) : [...s, g]));
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put('/me/goals', { goals: selected });
      setSelected(data.goals || []);
      setMessage('Your goals have been saved.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`${panelCls} ph-no-capture`} data-testid="goals-panel">
      <SectionHead eyebrow="What Matters To You" title="My Wellbeing Goals" />
      <p className="font-body-md text-body-md text-on-surface-variant mb-space-lg max-w-2xl leading-relaxed">
        Pick as many or as few as you like. We’ll use these to shape the guidance and habits we suggest. You can change
        them any time.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-space-md" data-testid="goal-options">
        {options.map((g) => {
          const on = selected.includes(g);
          return (
            <button
              key={g}
              type="button"
              data-testid={`goal-${g}`}
              aria-pressed={on}
              onClick={() => toggle(g)}
              className={`p-space-md rounded-lg border text-left transition-all flex items-start gap-space-sm ${on ? 'bg-primary-container border-surface-tint shadow-sm' : 'bg-surface border-surface-container hover:border-surface-tint/50'}`}
            >
              <Icon name={ICONS[g] || 'favorite'} className={`text-[20px] mt-0.5 ${on ? 'text-surface-tint' : 'text-on-surface-variant'}`} />
              <span className="flex-1">
                <span className="font-title-md text-body-md font-semibold text-outline block leading-snug">{LABELS[g] || g}</span>
                <span className={`font-label-sm text-label-sm uppercase tracking-wider ${on ? 'text-surface-tint font-bold' : 'text-on-surface-variant'}`}>
                  {on ? 'Selected' : 'Tap to choose'}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-space-md mt-space-lg pt-space-lg border-t border-surface-container">
        <button type="button" data-testid="goals-save" onClick={save} disabled={busy} className={primaryBtn}>
          <Icon name="save" className="text-[18px]" />
          <span>{busy ? 'Saving…' : 'Save my goals'}</span>
        </button>
        <span className="font-body-sm text-body-sm text-on-surface-variant">{selected.length} selected</span>
        {message ? <span data-testid="goals-message" className="font-body-sm text-body-sm text-primary font-semibold">{message}</span> : null}
      </div>
    </div>
  );
}
